import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { useTheme } from 'styled-components';
import * as Sentry from '@sentry/react';

// Components
import ModalContainer from '../../containers/ModalContainer';
import { InputRadioTagTable } from '../../../display/tables/InputRadioTagTable';

// Styled
import {
  Container,
  ButtonContainer,
  MainButtonStyled,
  CloseButton,
  PaginationButtonsStyled,
  EmptyMessage,
} from './styles';

// Types
import { RelateSegmentModalProps } from './types';
import { tableProps as InputRadioTagTableProps } from '../../../display/tables/InputRadioTagTable/types';
import { tableIds } from '../../../display/tables/types';
import { RouteSegment, SurveillanceRoute } from '../../../../types/surveillanceRoute';

// Utils
import { earlierIso } from '../../../../utils/avisoCloseWindow';
import { adaptSegmentToOrder } from '../../../../utils/surveillanceRoute';
import { endRoadIdOf } from '../../../../utils/roadStretches';
import { useRoadNamesById } from '../../../../hooks/useRoadDirectionLabels';

// GraphQL
import {
  GET_PENDING_EXTERNAL_SEGMENTS_MODAL,
  GET_ACTIVE_SURVEILLANCE_ROUTES_MODAL,
} from '../../../../gql/queries/surveillanceRoute';
import { GET_ORDERS_BY_IDS_SERVER } from '../../../../gql/queries/orders/orders';

// Redux
import { closeModal } from '../../../../state/actions/ui/modal';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { store } from '../../../../state/configureStore';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { useFreshTableCursor } from '../../../../hooks/useFreshTableCursor';
import {
  dateSortValue,
  pkSortValue,
  sortRows,
  type SortValue,
} from '../../../../utils/sortRows';
import { useOnlineStatus } from '../../../../offline/useOnlineStatus';

// V4 FR-15.3 — columnas no aplicables al dominio "tramo": a diferencia del
// listado RV (`SR_HIDDEN_COLUMN_IDS`), aquí tampoco aplican concesión/
// equip/carretera (el tramo hereda esos atributos del RV, no los expone por
// fila) ni 'state' (`pauseCause` no tiene variant de `SpecialTag` — ver
// `adaptSegmentToOrder`). Quedan visibles: select, roid, name (RV), fecha de
// registro, PK inici/fi, descripció.
// 'road' SALE de la lista el 2026-09-23: sala tiene que ver la carretera del
// aviso. Una patrulla que apoya al otro tramo deja avisos en vías que no son
// las de «su» recorrido, y el nombre del recorrido («Tramo 1…») no lo dice.
const SEGMENT_MODAL_HIDDEN_COLUMN_IDS = [
  'state',
  'capitol',
  'subCapitol',
  'predefinedOrder',
  'concession',
  'team',
  'track',
  'direction',
  'user',
  'creationDate',
];

// V4 FR-15.2 — filtros no aplicables al dominio "tramo".
//
// 2026-08-11: `description`, `expedient`, `index`, `pkInit` y `pkEnd` DEJAN de
// ocultarse. Estaban aquí por una razón que ya no se cumple —el endpoint no
// aceptaba esos criterios, así que eran inputs inertes que refetcheaban con las
// mismas variables— y ahora `FilterPendingExternalSegments` los acepta, con los
// mismos nombres que el listado de recorridos, y este modal los reenvía.
//
// Los que quedan siguen sin tener sentido para un tramo: hereda del recorrido
// la concesión, el equipo y la carretera, y su `state` no tiene variante de
// `SpecialTag` (ver `adaptSegmentToOrder`).
const SEGMENT_MODAL_HIDDEN_FILTER_IDS = [
  'capitol',
  'subCapitol',
  'predefinedOrder',
  'concession',
  'team',
  'classification',
  'track',
  'state',
];

// Con el recorrido FIJO (modo "elegir tramo para esta actuación", que se abre
// desde el propio detalle del RV) su nombre y su expediente son constantes en
// todas las filas: filtrar por ellos no puede quitar ni una, y un input que no
// hace nada es exactamente el problema que este trabajo viene a cerrar.
const SEGMENT_MODAL_HIDDEN_FILTER_IDS_ROUTE_FIXED = [
  ...SEGMENT_MODAL_HIDDEN_FILTER_IDS,
  'expedient',
];

/**
 * V4 P15 (2026-04-22) — modal reusable para vincular tramos desde sala /
 * desde detail de actuació. Ver FR-15 del doc V4.
 *
 * P15 rework #3 (2026-07-09) — REBUILD tras 2 rechazos del usuario del
 * enfoque "tabla hermana nueva" (v1 `RelateSegmentRow` hand-rolled dentro de
 * este mismo fichero; v2 `RouteSegmentTable`, tabla hermana separada — AMBAS
 * eliminadas). Esta versión no crea ninguna tabla: reutiliza
 * `InputRadioTagTable` TAL CUAL — el mismo componente compartido que ya usa
 * `SurveillanceRoutesPage` para listar RVs adaptando datos del dominio RV a
 * filas Order-like (`adaptSurveillanceRouteToOrder`). Aquí el adapter
 * gemelo es `adaptSegmentToOrder` (`utils/surveillanceRoute.ts`).
 *
 * `RelateSegmentModalData` (types.ts) NO cambia — los 3 puntos de invocación
 * (`WorkOrder`, `RouteChildOrders` vía `onActionRelateToSegment`, pill
 * "editar" en tramo/OT) siguen abriendo el modal igual
 * (`openModal(modalIds.RELATE_SEGMENT, data)`).
 */
/** Los 12 criterios que este modal manda al servidor. */
type AppliedFilters = Partial<{
  fromDate: string;
  toDate: string;
  fromEndDate: string;
  toEndDate: string;
  description: string;
  routeName: string;
  indexExpedition: number;
  indexType: number;
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
  // Orden de las cabeceras (2026-10-26): no viaja al servidor, se aplica aquí.
  sortField: string;
  orderBy: 'asc' | 'desc';
}>;

export const RelateSegmentModal = ({
  onClickClose,
  data,
}: RelateSegmentModalProps): JSX.Element => {
  const { t } = useTranslation(['modals', 'common']);
  const theme = useTheme();
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();

  const defaultPauseCauses = useMemo(
    () =>
      data.defaultPauseCauses ??
      (data.linkKind === 'external' ? ['external-call'] : ['action']),
    [data.defaultPauseCauses, data.linkKind],
  );

  // `InputRadioTagTable.setRowState` es un setter plano de STRING (el id de
  // la fila seleccionada) — mismo contrato que ya usa `SurveillanceRoutesPage`
  // (`setRowState={setSelectedRowId}`). A diferencia de la v2 (que necesitaba
  // un `rowStateRef` porque `RouteSegmentTable.setRowState` recibía la FILA
  // completa y su `onDoubleTapAccept` no traía el id como parámetro),
  // `InputRadioTagTable.onRowDoubleTap(row)` sí lo pasa — `handleConfirm`
  // acepta un id explícito para el caso doble-tap, sin necesidad de ref.
  const [selectedSegmentId, setSelectedSegmentId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // V4 PR6 (heredado v1) — evict del cache aislado al montar: sin esto,
    // reabrir el modal con las MISMAS variables mostraría el resultado STALE
    // cacheado antes de que llegue la respuesta fresca (`cache-and-network`).
    // Sin `args` (2026-07-10): desde que la query aísla por
    // `@connection(filter: ["input"])` hay un cajón POR MODO — evict a campo
    // completo los barre todos (el campo solo lo consume este modal).
    apolloClient.cache.evict({ fieldName: 'pendingExternalSegments' });
    apolloClient.cache.evict({ fieldName: 'activeSurveillanceRoutes' });
    apolloClient.cache.gc();

    setSelectedSegmentId('');
    // Solo al montar.
  }, []);

  // FR-15.1 — reset de TODOS los filtros y del orden al MONTAR.
  // `tableIds.SEGMENT_MODAL` es una key GLOBAL de Redux compartida por TODAS
  // las aperturas de este modal: sin este reset, reabrir el modal heredaría lo
  // que se filtró la última vez, en silencio (el bloque de filtros arranca
  // plegado en tablet). Desde 2026-10-26 lo hace `useFreshTableCursor`, con
  // la misma lista que la papelera de la tabla y ANTES de pintarla: los
  // campos Expediente e Índice cogen su valor una sola vez, al montarse.
  const isCursorFresh = useFreshTableCursor(tableIds.SEGMENT_MODAL);

  // Cursor VIVO: lo que hay escrito/elegido ahora mismo en el bloque de filtros.
  // Se lee para el `key` de la caché y para saber cuándo cambió algo, pero NO
  // alimenta la consulta.
  const globalCursor = useSelector(getCursor(tableIds.SEGMENT_MODAL), isEqual);

  // Filtros APLICADOS — los que de verdad viajan a la consulta.
  //
  // 🔴 Deliberadamente NO son el cursor vivo. En el resto de la app el cursor no
  // dispara nada por sí solo: cada página tiene un `onReFetchData` IMPERATIVO
  // que solo invocan la lupa y la papelera (ver `WorkOrdersPage`). Este modal
  // colgaba su `useQuery` del cursor vivo, así que Apollo reconsultaba en cuanto
  // cambiaba una variable — y era el ÚNICO sitio de la app donde elegir una
  // fecha filtraba al instante mientras que teclear un texto esperaba a la lupa.
  // Dos gestos distintos para la misma acción, dentro del mismo bloque.
  //
  // Copiando el cursor solo cuando la tabla pide refrescar, el modal se comporta
  // igual que las páginas: se filtra al pulsar, no al elegir.
  //
  // Arranca VACÍO, no con lo que haya en el store: el reset del cursor ocurre en
  // el efecto de montaje, o sea DESPUÉS del primer render. Si la foto inicial
  // saliera del store, la primera consulta iría filtrada con lo que quedó de la
  // última vez que se abrió el modal — justo lo que el reset existe para evitar.
  const [appliedCursor, setAppliedCursor] = useState<AppliedFilters>({});

  const {
    data: segmentsData,
    loading: loadingSegments,
    error: segmentsError,
    refetch: refetchSegments,
  } = useQuery(GET_PENDING_EXTERNAL_SEGMENTS_MODAL, {
    variables: {
      input: {
        surveillanceRouteId: data.defaultSurveillanceRouteId,
        fromDate: appliedCursor?.fromDate,
        toDate: appliedCursor?.toDate,
        fromEndDate: appliedCursor?.fromEndDate,
        // Tope de la OT cerrada: gana el menor entre lo que escribe el usuario
        // y el cierre + tolerancia. Sin tope, la consulta va como siempre.
        toEndDate: data.maxAvisoEndDate
          ? earlierIso(appliedCursor?.toEndDate, data.maxAvisoEndDate)
          : appliedCursor?.toEndDate,
        pauseCauses: defaultPauseCauses,
        // 2026-08-11 — el reenvío que faltaba. La tabla compartida ya volcaba
        // todos estos valores al cursor al pulsar la lupa; lo que no había era
        // nadie que los leyera, así que Nombre y Descripción se veían pero no
        // filtraban. Las claves del cursor coinciden 1:1 con las del input
        // GraphQL a propósito: un renombrado a medias volvería a producir un
        // filtro mudo, sin error.
        description: appliedCursor?.description,
        routeName: appliedCursor?.routeName,
        indexExpedition: appliedCursor?.indexExpedition,
        indexType: appliedCursor?.indexType,
        pkInitKm: appliedCursor?.pkInitKm,
        pkInitMeter: appliedCursor?.pkInitMeter,
        pkEndKm: appliedCursor?.pkEndKm,
        pkEndMeter: appliedCursor?.pkEndMeter,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: routesData,
    loading: loadingRoutes,
    refetch: refetchRoutes,
  } = useQuery(GET_ACTIVE_SURVEILLANCE_ROUTES_MODAL, {
    variables: { input: {} },
    fetchPolicy: 'cache-and-network',
  });

  const segments: RouteSegment[] = useMemo(
    () => segmentsData?.pendingExternalSegments ?? [],
    [segmentsData],
  );
  const activeRoutes: SurveillanceRoute[] = useMemo(
    () => routesData?.activeSurveillanceRoutes ?? [],
    [routesData],
  );

  const routeNameById = useMemo(() => {
    const map = new Map<string, string>();
    activeRoutes.forEach((r) => map.set(r.id, r.name || r.id.slice(0, 6)));
    return map;
  }, [activeRoutes]);

  // Columna "Recorregut" — NOMBRE real del recorrido desde 2026-08-11.
  //
  // Antes se pintaba `Su.0000322`, derivado del número de expedición del roid
  // del propio tramo, porque resolver el nombre por fila habría sido un N+1 de
  // jerarquía Order. Ahora el backend lo trae ya resuelto EN LOTE
  // (`RouteSegment.surveillanceRoute`), así que se pinta lo que el operario
  // escribió: «Tramo-1-turno-tarde». Es además lo que hace buscable el filtro
  // de Nombre.
  //
  // Dos respaldos, en este orden, y ambos por CONTENIDO (no por `undefined`):
  // un recorrido puede existir con el nombre vacío, y un backend anterior a
  // este campo no lo devuelve. El código `Su.NNNN` sigue identificando la fila
  // igual de bien, así que la columna nunca queda muda.
  const defaultRouteName = data.defaultSurveillanceRouteId
    ? routeNameById.get(data.defaultSurveillanceRouteId) ?? ''
    : '';
  const routeNameForSegment = (
    segment: RouteSegment,
    order?: { roid?: string },
  ): string => {
    const realName = segment.surveillanceRoute?.name?.trim();
    if (realName) return realName;
    if (defaultRouteName.trim()) return defaultRouteName;
    const expedition =
      order?.roid?.split('.')?.[1] ??
      (segment.indexExpedition != null
        ? String(segment.indexExpedition).padStart(7, '0')
        : undefined);
    return expedition ? `Su.${expedition}` : '—';
  };

  // Resolución batched de roid+description (segment.orderIDs[0] → Order),
  // mismo patrón que `RouteDetailed/loader.ts#resolveOrderRoid` — UNA query
  // con TODOS los orderIDs visibles, no N+1.
  const [ordersById, setOrdersById] = useState(
    new Map<string, { roid?: string; description?: string }>(),
  );
  const [isResolvingOrders, setIsResolvingOrders] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const resolveOrders = async () => {
      const ids = Array.from(
        new Set(
          segments
            .map((s) => s.orderIDs?.[0])
            .filter((id): id is string => Boolean(id)),
        ),
      );
      if (ids.length === 0) {
        if (!cancelled) setOrdersById(new Map());
        return;
      }
      setIsResolvingOrders(true);
      try {
        const { data: ordersData } = await apolloClient.query({
          query: GET_ORDERS_BY_IDS_SERVER,
          variables: { ids },
          fetchPolicy: 'cache-first',
        });
        if (!cancelled) {
          setOrdersById(
            new Map(
              (ordersData?.ordersByIds ?? []).map(
                (o: { id: string; roid: string; description?: string }) =>
                  [o.id, { roid: o.roid, description: o.description }] as const,
              ),
            ),
          );
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: 'surveillance-route', component: 'RelateSegmentModal' },
        });
      } finally {
        if (!cancelled) setIsResolvingOrders(false);
      }
    };
    resolveOrders().catch((err) => {
      Sentry.captureException(err);
      if (!cancelled) setIsResolvingOrders(false);
    });
    return () => {
      cancelled = true;
    };
  }, [segments, apolloClient]);

  // Columna «Carretera» (2026-09-23): la vía donde ACABÓ el tramo, que es
  // donde la patrulla paró a avisar. Un tramo de un recorrido de UNA vía guarda
  // `roadId` nulo —la suya es la primera del recorrido—, de ahí el respaldo.
  const roadIdForSegment = (segment: RouteSegment): string | undefined =>
    endRoadIdOf(segment) ?? segment.surveillanceRoute?.roadIDs?.[0] ?? undefined;
  const roadNames = useRoadNamesById(
    segments
      .map(roadIdForSegment)
      .filter((id): id is string => Boolean(id)),
  );

  const sortField = appliedCursor.sortField ?? '';
  const sortDirection = appliedCursor.orderBy === 'asc' ? 'asc' : 'desc';

  const adaptedRows = useMemo(() => {
    const pairs = segments.map((segment) => {
      const orderId = segment.orderIDs?.[0];
      const order = orderId ? ordersById.get(orderId) : undefined;
      const roadId = roadIdForSegment(segment);
      const row = adaptSegmentToOrder(
        segment,
        routeNameForSegment(segment, order),
        order,
        roadId ? roadNames[roadId] : undefined,
      );
      return { segment, row };
    });
    // Orden de las cabeceras (2026-10-26): antes cambiaban la flecha y no las
    // filas, porque el servidor no ordena esta bandeja y nadie leía el cursor.
    // Sin cabecera elegida se queda el orden del servidor (inicio más reciente).
    type Pair = (typeof pairs)[number];
    const valueOf: Record<string, (pair: Pair) => SortValue> = {
      state: ({ row }) => row.state,
      name: ({ row }) => row.name,
      registerInitDate: ({ segment }) =>
        dateSortValue(segment.registerInitDate || segment.startDate),
      pkInitKm: ({ segment }) => pkSortValue(segment.pkInitKm, segment.pkInitMeter),
      pkEndKm: ({ segment }) => pkSortValue(segment.pkEndKm, segment.pkEndMeter),
    };
    const sorted = valueOf[sortField]
      ? sortRows(pairs, valueOf[sortField], sortDirection)
      : pairs;
    return sorted.map(({ row }) => row);
  }, [segments, ordersById, defaultRouteName, roadNames, sortField, sortDirection]);

  // Paginación EN CLIENTE (patrón footer de `RelatesCommunicationModal`):
  // `pendingExternalSegments` devuelve un array plano sin cursor, así que la
  // "página" es un slice local. PAGE_SIZE=8 = el `take` por defecto de los
  // modales canónicos (GENERIC_ORDER_MODAL/CAMPAIGN_MODAL).
  const PAGE_SIZE = 8;
  // Lo que se puede elegir ahora (todas las páginas): si un filtro quita el
  // tramo elegido, la tabla suelta la selección y «Vincular» se desactiva.
  const selectableRowIds = useMemo(
    () => adaptedRows.map((row) => row.id),
    [adaptedRows],
  );
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    // Nuevos resultados (buscar/limpiar filtros/refetch) u otro orden → vuelta
    // a página 1.
    setCurrentPage(1);
  }, [segments, sortField, sortDirection]);
  const pageCount = Math.max(1, Math.ceil(adaptedRows.length / PAGE_SIZE));
  const pagedRows = useMemo(
    () =>
      adaptedRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [adaptedRows, currentPage],
  );

  // Combina las 2 queries + la resolución async de Orders — evita un flash
  // de "0 resultados" mientras roid/description todavía se están
  // resolviendo tras llegar los segmentos.
  const { isOnline } = useOnlineStatus();
  const loading = loadingSegments || loadingRoutes || isResolvingOrders;

  // El «cargando» de la tabla (23-oct, el usuario): la tabla compartida solo
  // lo pinta con el flag de Redux de su `tableId`, y aquí solo se encendía al
  // vincular. Mientras llega la lista, también. Durante el envío manda el suyo.
  useEffect(() => {
    if (isSubmitting) return;
    dispatch(loadingData(tableIds.SEGMENT_MODAL, loading));
  }, [dispatch, isSubmitting, loading]);
  useEffect(
    () => () => {
      dispatch(loadingData(tableIds.SEGMENT_MODAL, false));
    },
    [dispatch],
  );

  const onReFetchData = useCallback(async () => {
    // Se lee del store, no del `globalCursor` de este render: la papelera
    // despacha el cursor limpio y llama aquí en el MISMO tick, así que la
    // closure todavía tendría los filtros viejos. Mismo motivo por el que
    // `WorkOrdersPage` usa `store.getState()`.
    const fresco = getCursor(tableIds.SEGMENT_MODAL)(store.getState());
    const sinCambios = isEqual(fresco, appliedCursor);
    setAppliedCursor(fresco);
    // Si los filtros CAMBIARON, la propia foto nueva reconsulta (Apollo reacciona
    // a las variables). Si NO cambiaron —el botón de recargar, o buscar dos
    // veces seguidas— no habría consulta, así que se fuerza a mano.
    await Promise.all([
      sinCambios ? refetchSegments() : Promise.resolve(),
      refetchRoutes(),
    ]);
  }, [appliedCursor, refetchSegments, refetchRoutes]);

  // No-op intencional: `pendingExternalSegments` devuelve un array PLANO sin
  // cursor/paginación real (deuda "dormant" ya documentada en el backend) —
  // no hay una "página siguiente" que pedir a red. `InputRadioTagTable` solo
  // invoca esto tras next/previous, y como `pageInfo` de abajo es SIEMPRE
  // `{hasNextPage:false, hasPreviousPage:false}` sus propios guards ya
  // deshabilitan esa navegación antes de llegar aquí.
  const onReFetchPagination = useCallback(() => {
    // Intencionalmente vacío — ver comentario arriba.
  }, []);

  const handleConfirm = useCallback(
    async (segmentId?: string) => {
      const id = segmentId ?? selectedSegmentId;
      if (!id) return;
      try {
        setIsSubmitting(true);
        // Loading canónico mientras se ejecuta la mutation de vincular
        // (feedback usuario 2026-07-10): el flag de tabla hace que
        // InputRadioTagTable muestre su Loader superpuesto y se atenúe —
        // el mismo mecanismo que usan todas las tablas de la app.
        dispatch(loadingData(tableIds.SEGMENT_MODAL, true));
        await data.onConfirm(id);
        dispatch(closeModal());
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            feature: 'surveillance-route',
            component: 'RelateSegmentModal',
            linkKind: data.linkKind,
          },
        });
      } finally {
        dispatch(loadingData(tableIds.SEGMENT_MODAL, false));
        setIsSubmitting(false);
      }
    },
    [data, dispatch, selectedSegmentId],
  );

  const tableData = useMemo(
    () => ({
      // Solo la página actual — la paginación (slice cliente) la maneja el
      // footer del modal, no la tabla (su footer interno va apagado).
      genericOrders: pagedRows,
      filtersDataOptions: {
        concessions: [],
        capitols: [],
        subCapitols: [],
        classifications: [],
      },
      totalCount: adaptedRows.length,
      pageInfo: { hasNextPage: false, hasPreviousPage: false },
      loading,
    }),
    [pagedRows, adaptedRows.length, loading],
  );

  return (
    // Composición canónica de `RelatesCommunicationModal`: SIN prop `title`
    // (ese banner sobraba — feedback usuario 2026-07-09; el único título es
    // el "Filtrar X per:" de la propia tabla) y footer Volver | paginación |
    // acción.
    <ModalContainer onClickClose={onClickClose} fill={theme.colors.greyBoard}>
      <Container>
        {/*
          Tabla routing-agnóstica desde 2026-07-09: `InputRadioTagTable` ya
          NO llama `useNavigate()` internamente, así que se monta en un modal
          (que vive FUERA del árbol de React Router — `SuperModal` en App.tsx
          es HERMANO de `<Router />`) SIN ningún wrapper. El doble-tap lo
          define este consumidor: seleccionar + confirmar en el mismo gesto
          (paridad con `onDoubleTapAccept` de InputRadioDefaultTable/
          RelatedCampaignTable).
        */}
        {isCursorFresh && (
        <InputRadioTagTable
          titleText={t('modals:relateSegment.filterBy')}
          data={tableData as unknown as InputRadioTagTableProps['data']}
          setRowState={setSelectedSegmentId}
          onReFetchData={onReFetchData}
          onReFetchPagination={onReFetchPagination}
          tableId={tableIds.SEGMENT_MODAL}
          hiddenColumnIds={SEGMENT_MODAL_HIDDEN_COLUMN_IDS}
          hiddenFilterIds={
            data.defaultSurveillanceRouteId
              ? SEGMENT_MODAL_HIDDEN_FILTER_IDS_ROUTE_FIXED
              : SEGMENT_MODAL_HIDDEN_FILTER_IDS
          }
          showRouteNameColumn
          // La COLUMNA del recorrido se pinta siempre; el INPUT de nombre solo
          // tiene sentido cuando el recorrido es libre (modo sala). Con el
          // recorrido fijo todas las filas comparten nombre y filtrar por él no
          // puede quitar ninguna.
          showRouteNameFilter={!data.defaultSurveillanceRouteId}
          // Un tramo tiene inicio y fin: hasta 2026-08-11 solo se podía acotar
          // por el inicio, aunque la tabla pinta las dos fechas.
          showEndDateFilter
          showButtonsPagination={false}
          // Solo con la lista ya completa: mientras recarga, la lista está
          // vacía un instante y el tramo elegido se soltaría sin motivo.
          selectableRowIds={loading ? undefined : selectableRowIds}
          onRowDoubleTap={(row) => {
            if (row.original.id) {
              setSelectedSegmentId(row.original.id);
              handleConfirm(row.original.id);
            }
          }}
        />
        )}
        {/* Estado vacío explícito — la decisión de UX es mantener el modal
            abierto (tiene filtros: el usuario puede ampliar fechas/quitar
            criterios) pero DECIR por qué no hay filas, en vez de una tabla
            muda o un toast sin contexto.
            Error ≠ vacío (2026-07-21): un fallo de la query (p.ej. el
            Forbidden por roles que dejó al Tablet sin lista) se pintaba como
            "no hay tramos" y disfrazaba el bug de dato-roto — si la query
            falló, decirlo. */}
        {!loading && segmentsError && (
          <EmptyMessage>
            {/* Sin cobertura esta lista la sirve el servidor y no llega. Decir
                "no hay tramos" sería mentir y el operario buscaría el fallo
                donde no está: la lista está vacía porque no hay red, y eso se
                arregla solo al recuperarla. */}
            {isOnline
              ? t('common:messages.error')
              : t('common:offline.noConnection')}
          </EmptyMessage>
        )}
        {!loading && !segmentsError && adaptedRows.length === 0 && (
          <EmptyMessage>
            {isOnline
              ? t('modals:relateSegment.empty')
              : t('common:offline.noConnection')}
          </EmptyMessage>
        )}
        <ButtonContainer>
          <CloseButton
            text={t('common:buttons.back')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isSubmitting}
          />
          <PaginationButtonsStyled
            pageCount={pageCount}
            currentPage={currentPage}
            totalCount={adaptedRows.length}
            nextPage={() =>
              setCurrentPage((prev) => Math.min(prev + 1, pageCount))
            }
            previousPage={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            isDisabled={loading}
          />
          <MainButtonStyled
            text={t('modals:relateSegment.link')}
            onClick={() => handleConfirm()}
            disabled={!selectedSegmentId || isSubmitting || Boolean(data.isLoading)}
          />
        </ButtonContainer>
      </Container>
    </ModalContainer>
  );
};

export default RelateSegmentModal;
